import { useEffect, useRef, useState } from "react";
import { useInView, animate } from "framer-motion";
import Reveal, { EASE } from "@/components/Reveal";
import { Building2, BriefcaseBusiness, UserCheck, Clock } from "lucide-react";

function Counter({ to, suffix = "", from = 0, format = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(from);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(from, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, from, to]);

  return (
    <span ref={ref}>
      {format ? val.toLocaleString("en-US") : val}
      {suffix}
    </span>
  );
}

const metrics = [
  { id: "metric-brands", icon: Building2, render: () => <Counter to={9} />, label: "Brands that have trusted me with their presence." },
  { id: "metric-years", icon: BriefcaseBusiness, render: () => <Counter to={8} suffix="+" />, label: "Years of professional experience." },
  { id: "metric-one-to-one", icon: UserCheck, render: () => "1:1", label: "Every engagement led by me personally. No account layers." },
  { id: "metric-availability", icon: Clock, render: () => <Counter to={24} suffix="/7" />, label: "Available for clients, across time zones." },
];

export default function NumbersProof() {
  return (
    <section className="bg-[#118C4F] text-[#09090B]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <Reveal>
          <h2
            data-testid="numbers-heading"
            className="font-serif-d font-black tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl"
          >
            Numbers worth <span className="italic font-semibold opacity-55">knowing.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-14 mt-16 sm:mt-20">
          {metrics.map((m, i) => (
            <Reveal key={m.id} delay={i * 0.1}>
              <div data-testid={m.id}>
                <m.icon size={22} strokeWidth={1.8} className="text-[#09090B]/70" aria-hidden="true" />
                <p className="font-serif-d font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4">
                  {m.render()}
                </p>
                <p className="text-xs sm:text-sm text-[#09090B]/65 leading-relaxed mt-3 max-w-[22ch]">
                  {m.label}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
